/**
 * useTrialGuard - Hook pour vérifier si le trial est expiré et rediriger vers /pricing
 * À utiliser dans les composants qui nécessitent un abonnement actif
 *
 * SUPERADMIN bypass: Les super administrateurs ne sont jamais redirigés
 */

import { useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { subscriptionsAPI } from '@/utils/api';
import { useAuthStore } from '@/stores/authStore';

export interface TrialGuardResult {
  isLoading: boolean;
  isTrial: boolean;
  isExpired: boolean;
  daysRemaining: number;
  trialEndsAt: Date | null;
  shouldRedirect: boolean;
  isSuperAdmin: boolean;
}

// Pages qui ne nécessitent pas d'abonnement actif
const ALLOWED_PATHS = [
  '/pricing',
  '/checkout',
  '/onboarding',
  '/settings',
  '/logout',
  '/superadmin', // SuperAdmin pages are always allowed
];

export function useTrialGuard(): TrialGuardResult {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();

  // SUPERADMIN bypass - never redirect superadmins
  const isSuperAdmin = user?.role === 'SUPERADMIN';

  // Récupérer l'abonnement courant (skip for superadmin)
  // Cache court pour détecter rapidement les changements de plan (upgrade, trial expiré)
  const { data: subscription, isLoading } = useQuery({
    queryKey: ['current-subscription'],
    queryFn: () => subscriptionsAPI.getCurrent(),
    staleTime: 10 * 1000, // Cache 10 secondes seulement (critique pour la redirection)
    gcTime: 30 * 1000, // Garbage collect après 30 secondes
    retry: 1,
    enabled: !isSuperAdmin,
    refetchOnWindowFocus: true, // Refetch quand l'utilisateur revient sur l'onglet
    refetchOnMount: 'always', // Toujours refetch au mount
  });

  const result = useMemo((): TrialGuardResult => {
    // SUPERADMIN bypass - never restrict superadmins
    if (isSuperAdmin) {
      return {
        isLoading: false,
        isTrial: false,
        isExpired: false,
        daysRemaining: 999,
        trialEndsAt: null,
        shouldRedirect: false,
        isSuperAdmin: true,
      };
    }

    if (!subscription) {
      return {
        isLoading: true,
        isTrial: false,
        isExpired: false,
        daysRemaining: 0,
        trialEndsAt: null,
        shouldRedirect: false,
        isSuperAdmin: false,
      };
    }

    // Vérifier si c'est un plan TRIAL
    const planCode = (subscription as any).plan?.code || (subscription as any).planId || '';
    const isTrial = planCode.toUpperCase().includes('TRIAL');

    if (!isTrial || !subscription.trialEndsAt) {
      return {
        isLoading: false,
        isTrial: false,
        isExpired: false,
        daysRemaining: 0,
        trialEndsAt: null,
        shouldRedirect: false,
        isSuperAdmin: false,
      };
    }

    const trialEnd = new Date(subscription.trialEndsAt);
    const now = new Date();
    const diffMs = trialEnd.getTime() - now.getTime();
    const daysRemaining = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
    const isExpired = diffMs <= 0;

    // Vérifier si on doit rediriger
    const currentPath = location.pathname;
    const isAllowedPath = ALLOWED_PATHS.some(path => currentPath.startsWith(path));
    const shouldRedirect = isExpired && !isAllowedPath;

    return {
      isLoading: false,
      isTrial: true,
      isExpired,
      daysRemaining,
      trialEndsAt: trialEnd,
      shouldRedirect,
      isSuperAdmin: false,
    };
  }, [subscription, location.pathname, isSuperAdmin]);

  // Effectuer la redirection si nécessaire
  useEffect(() => {
    if (!isLoading && result.shouldRedirect) {
      navigate('/pricing?expired=true', { replace: true });
    }
  }, [isLoading, result.shouldRedirect, navigate]);

  return { ...result, isLoading };
}
